import { computeNextOccurrence, isOnOrBefore, isInRange } from './compute-next-occurrence';
import type { RecurrenceRule } from './types';

export interface DueRule extends RecurrenceRule {
  id: string;
  isActive: boolean;
  autoGenerate: boolean;
  nextOccurrenceDate: string;
}

export interface DueOccurrence {
  ruleId: string;
  occurrenceDate: string;
}

export interface RuleGenerationResult {
  ruleId: string;
  occurrences: string[];
  /** Updated nextOccurrenceDate after running through occurrences. */
  newNextOccurrenceDate: string;
  /** Last occurrence emitted, or null if none. */
  lastGeneratedDate: string | null;
}

/**
 * For a single rule, return all occurrences whose date is on or before `today`,
 * along with the next-occurrence date to persist back. Stops at end_date.
 *
 * If the rule is inactive or auto_generate=false, no occurrences are emitted.
 */
export function generateOccurrencesForRule(rule: DueRule, today: string): RuleGenerationResult {
  const occurrences: string[] = [];
  let cursor = rule.nextOccurrenceDate;

  if (rule.isActive && rule.autoGenerate) {
    while (
      isOnOrBefore(cursor, today) &&
      isInRange(cursor, rule.startDate, rule.endDate)
    ) {
      occurrences.push(cursor);
      cursor = computeNextOccurrence(rule, cursor);
    }
  }

  return {
    ruleId: rule.id,
    occurrences,
    newNextOccurrenceDate: cursor,
    lastGeneratedDate: occurrences.length > 0 ? occurrences[occurrences.length - 1]! : null,
  };
}

/**
 * Bulk variant: for each rule, compute due occurrences. Returns flat list of
 * (ruleId, occurrenceDate) plus per-rule update payloads.
 */
export function generateDuePayments(
  rules: DueRule[],
  today: string,
): { dueOccurrences: DueOccurrence[]; ruleUpdates: RuleGenerationResult[] } {
  const ruleUpdates = rules.map((r) => generateOccurrencesForRule(r, today));
  const dueOccurrences: DueOccurrence[] = [];
  for (const u of ruleUpdates) {
    for (const occ of u.occurrences) {
      dueOccurrences.push({ ruleId: u.ruleId, occurrenceDate: occ });
    }
  }
  return { dueOccurrences, ruleUpdates };
}
